import { maintenance } from "@/shared/lib/maintenance";
import type { components } from "./schema";

export type S = components["schemas"];

/** A problem-details error from the API (RFC 9457 with a stable code). */
export class ApiError extends Error {
  status: number;
  code: string;
  detail: string;
  fields: { field: string; message: string }[];
  constructor(status: number, code: string, detail: string, fields: { field: string; message: string }[] = []) {
    super(detail || code);
    this.status = status;
    this.code = code;
    this.detail = detail;
    this.fields = fields;
  }
}

function cookie(name: string): string {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : "";
}

const COMPANY_KEY = "clientos.company";
export const currentCompany = {
  get: () => localStorage.getItem(COMPANY_KEY) ?? "",
  set: (id: string) => localStorage.setItem(COMPANY_KEY, id),
};

type Query = Record<string, string | number | boolean | undefined | null | Record<string, string>>;

export function qs(query?: Query): string {
  if (!query) return "";
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "") continue;
    if (typeof v === "object") {
      for (const [fk, fv] of Object.entries(v)) if (fv !== "" && fv != null) p.set(`${k}[${fk}]`, String(fv));
    } else p.set(k, String(v));
  }
  const s = p.toString();
  return s ? "?" + s : "";
}

export interface RequestOptions {
  query?: Query;
  body?: unknown;
  headers?: Record<string, string>;
  /** The body as it comes (an image, a file), not JSON. */
  blob?: boolean;
}

/** Sends a request to /api and returns the parsed body (or undefined for 204). */
export async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json", ...opts.headers };
  let body: BodyInit | undefined;
  if (opts.body instanceof FormData) body = opts.body;
  else if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }
  if (method !== "GET") headers["X-CSRF-Token"] = cookie("clientos_csrf");
  const company = currentCompany.get();
  if (company) headers["X-Company-ID"] = company;
  const res = await fetch("/api" + path + qs(opts.query), { method, headers, body, credentials: "same-origin" });
  if (res.status === 204) return undefined as T;
  if (opts.blob && res.ok) return (await res.blob()) as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const p = (data ?? {}) as { code?: string; detail?: string; errors?: { field: string; message: string }[] };
    if (res.status === 503 && p.code === "platform.maintenance") maintenance.close();
    throw new ApiError(res.status, p.code ?? "request.failed", p.detail ?? res.statusText, p.errors ?? []);
  }
  if (res.headers.get("ETag") && data && typeof data === "object") {
    Object.defineProperty(data, "__etag", { value: res.headers.get("ETag"), enumerable: false });
  }
  return data as T;
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>("GET", path, { query }),
  post: <T>(path: string, body?: unknown, headers?: Record<string, string>) => request<T>("POST", path, { body: body ?? {}, headers }),
  put: <T>(path: string, body: unknown, etag?: string) =>
    request<T>("PUT", path, { body, headers: etag ? { "If-Match": etag } : undefined }),
  del: (path: string, body?: unknown) => request<void>("DELETE", path, body === undefined ? undefined : { body }),
  /** POSTs, and returns what comes back as a Blob (an image, a file). */
  postBlob: (path: string, body: unknown) => request<Blob>("POST", path, { body, blob: true }),
  upload: <T>(path: string, query: Query, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return request<T>("POST", path, { query, body: fd });
  },
};

/** ETag captured from the last GET of a record, for optimistic concurrency. */
export function etagOf(rec: unknown): string | undefined {
  return rec && typeof rec === "object" ? (rec as { __etag?: string }).__etag : undefined;
}

export interface ListBody<T> {
  items: T[];
  next_cursor?: string;
  total: number;
}
